// The audio engine, and the one elegant part (SPEC §16).
//
// "Browsers block audio until a user gesture. In this game the first gesture is
// A CLICK ON THE MAP, which is also the core verb — so audio unlocks on the
// player's first attack, with no 'enable sound' interstitial anywhere. Remember
// the choice per token."
//
// That is the whole design and it is worth stating what it forbids, because the
// forbidden thing is what every other game does: there is no enable-sound modal,
// no speaker button that must be pressed before anything is audible, and no
// autoplay attempt on load that the browser refuses and that leaves the interface
// showing a muted icon it cannot explain. The AudioContext is not even
// constructed until the first click, so there is nothing to be blocked.
//
// Everything else here is plumbing around that: three buses (mixer.ts), the
// synthesised UI layer (synth.ts), and two asset banks that are absent (
// announcer.ts). The engine never fails a call — a click before the unlock is a
// no-op, not a throw, because the click is a game action first and a sound
// second.

import { SilentImpactBank, SilentVoiceBank, type AnnouncerRequest, type ImpactBank, type VoiceBank } from './announcer';
import type { ResumableContext } from './graph';
import {
  BusEffects,
  BusVoice,
  Mixer,
  loadVolumes,
  saveVolumes,
  type Bus,
  type VolumeStore,
} from './mixer';
import { playImpact, playUI, type UISound, type VoiceOptions } from './synth';

export const UnlockedKey = 'wwp.audio.unlocked';

export interface AudioEngineOptions {
  /**
   * Builds the context. Injected so a test never constructs a real one, and so
   * the engine can be told to use an OfflineAudioContext for a capture.
   */
  create?: () => ResumableContext | null;
  store?: VolumeStore | null;
  voice?: VoiceBank;
  impacts?: ImpactBank;
  /**
   * Whether a previous session already unlocked. §16: "Remember the choice per
   * token." The choice is remembered, but the CONTEXT is still not built until a
   * gesture — a remembered unlock does not survive the browser's autoplay policy,
   * only the interface's memory of the player's intent does.
   */
  unlocked?: boolean;
}

/** Reads the remembered unlock. */
export function rememberedUnlock(store?: VolumeStore | null): boolean {
  if (!store) return false;
  try {
    return store.getItem(UnlockedKey) === '1';
  } catch {
    return false;
  }
}

export class AudioEngine {
  private ctx: ResumableContext | null = null;
  private mixer: Mixer | null = null;
  private readonly make: () => ResumableContext | null;
  private readonly store: VolumeStore | null;
  private readonly voiceBank: VoiceBank;
  private readonly impactBank: ImpactBank;
  private wanted: boolean;
  /** Volumes are readable and settable before the context exists. */
  private pending: Record<Bus, number> | null = null;

  constructor(opts: AudioEngineOptions = {}) {
    this.make = opts.create ?? defaultContextFactory;
    this.store = opts.store ?? null;
    this.voiceBank = opts.voice ?? SilentVoiceBank;
    this.impactBank = opts.impacts ?? SilentImpactBank;
    this.wanted = opts.unlocked ?? rememberedUnlock(this.store);
  }

  /** Whether audio is running. */
  live(): boolean {
    return this.ctx !== null && this.mixer !== null;
  }

  /** Whether the player has ever unlocked, across sessions. */
  remembered(): boolean {
    return this.wanted;
  }

  /**
   * The unlock. Call it from the map click handler and from nowhere else.
   *
   * Idempotent, synchronous from the caller's point of view, and it never throws:
   * a browser that refuses to build a context must not take the click with it.
   */
  unlock(): boolean {
    this.wanted = true;
    this.persistUnlock();
    if (this.ctx) {
      void this.ctx.resume().catch(() => {});
      return true;
    }
    let ctx: ResumableContext | null = null;
    try {
      ctx = this.make();
    } catch {
      return false;
    }
    if (!ctx) return false;
    this.ctx = ctx;
    this.mixer = new Mixer(ctx, ctx.destination, this.store);
    if (this.pending) {
      for (const [bus, v] of Object.entries(this.pending)) this.mixer.setVolume(bus as Bus, v);
      this.pending = null;
    }
    if (ctx.state !== 'running') void ctx.resume().catch(() => {});
    return true;
  }

  private persistUnlock(): void {
    if (!this.store) return;
    try {
      this.store.setItem(UnlockedKey, '1');
    } catch {
      // Private window. The unlock still holds for this session.
    }
  }

  volumes(): Record<Bus, number> {
    if (this.mixer) return this.mixer.volumes();
    if (!this.pending) this.pending = loadPending(this.store);
    return { ...this.pending };
  }

  volume(bus: Bus): number {
    return this.volumes()[bus];
  }

  /**
   * Moves one slider.
   *
   * Works before the unlock: the panel is visible and draggable from the first
   * frame, and a player who turns music down before their first click must not
   * have that undone by the click.
   */
  setVolume(bus: Bus, v: number): void {
    if (this.mixer) {
      this.mixer.setVolume(bus, v, this.ctx ?? undefined);
      return;
    }
    if (!this.pending) this.pending = loadPending(this.store);
    this.pending[bus] = v < 0 ? 0 : v > 1 ? 1 : v;
    savePending(this.store, this.pending);
  }

  /** A UI sound on the effects bus. Silent, not an error, before the unlock. */
  ui(sound: UISound, opts: VoiceOptions = {}): void {
    if (!this.ctx || !this.mixer) return;
    try {
      playUI(this.ctx, this.mixer.bus(BusEffects), sound, opts);
    } catch {
      // A voice that fails to build must never take a frame with it.
    }
  }

  /**
   * An impact.
   *
   * The generated bank gets first refusal — it is the bespoke, directable set §16
   * describes — and the synth is what plays while the bank is absent, which today
   * is always. A shot that lands silently would be a worse bug than a shot that
   * sounds generic.
   */
  impact(weight: number, kill = false, siege = false): void {
    if (this.impactBank.ready() && this.impactBank.hit({ weight, kill, siege })) return;
    if (!this.ctx || !this.mixer) return;
    try {
      playImpact(this.ctx, this.mixer.bus(BusEffects), weight, kill);
    } catch {
      // Same rule as ui().
    }
  }

  /**
   * The announcer.
   *
   * Returns whether anything was spoken, so the caller can see that the feed line
   * is doing all the work — which it is, and which §16 requires it to be able to
   * do on its own.
   */
  announce(req: AnnouncerRequest): boolean {
    if (!this.voiceBank.ready()) return false;
    // Routed to its own bus so muting music leaves it audible, which is the whole
    // reason there are three buses.
    void this.mixer?.bus(BusVoice);
    return this.voiceBank.say(req);
  }

  /** Releases the context. */
  async dispose(): Promise<void> {
    const ctx = this.ctx;
    this.ctx = null;
    this.mixer = null;
    if (ctx?.close) await ctx.close().catch(() => {});
  }
}

function loadPending(store: VolumeStore | null): Record<Bus, number> {
  // Imported lazily through the same loader the mixer uses, so the defaults and
  // the persisted shape can never diverge between "before unlock" and "after".
  return loadVolumes(store);
}

function savePending(store: VolumeStore | null, v: Record<Bus, number>): void {
  saveVolumes(store, v);
}


/**
 * The real context, built only when asked.
 *
 * `webkitAudioContext` is still the only constructor on older iOS Safari, and an
 * iPad is a very plausible way to leave a tab open for four weeks.
 */
function defaultContextFactory(): ResumableContext | null {
  const g = globalThis as unknown as {
    AudioContext?: new () => ResumableContext;
    webkitAudioContext?: new () => ResumableContext;
  };
  const Ctor = g.AudioContext ?? g.webkitAudioContext;
  if (!Ctor) return null;
  return new Ctor();
}
